import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, CircularProgress, Grid, Link, Typography } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import { AxiosError } from 'axios'
import { client } from '../utils/client'
import {
  MarketplaceColdStartPayloadType,
  MarketplaceThemeType,
  ProfessionType,
  RegistrationGroupType,
} from '../utils/sharedTypes'
import {
  CartBar,
  CatalogSections,
  IdentityForm,
  MarketplaceHero,
  MarketplaceShell,
  ProductDetailModal,
  ProfessionPicker,
  ThemeCard,
} from '../components'
import { toCartItem } from '../components/marketplace/marketplaceCatalog'
import {
  EMPTY_IDENTITY,
  MarketplaceIdentity,
  buildCatalogueQuery,
  buildVisitPayload,
  hasIdentity,
  identityRequested,
  readIdentityFromUrl,
  hidesPrice,
  restrictToFunder,
  flattenIgnoredParameters,
  forgetRememberedGroupId,
  isLockedFunder,
  readRememberedGroupId,
  readTrackingParameters,
  readVisitorId,
  rememberGroupId,
  resolveDisplayDomain,
} from '../utils/marketplaceColdStart'
import { useCart } from '../hooks/useCart'

// Marketplace sans groupe : le profil est décrit par l'URL du lien cliqué (campagne, QR code, site
// vitrine) au lieu d'être déduit d'un dossier existant. Rien n'est créé en base avant « Continuer ».
// Cf. docs/marketplace-cold-start.md.

const COLD_START_CART_SCOPE = 'cold_start'

const MarketplaceColdStart = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const [payload, setPayload] = useState<MarketplaceColdStartPayloadType | null>(null)
  const [error, setError] = useState<'network' | null>(null)
  const [openedTheme, setOpenedTheme] = useState<MarketplaceThemeType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null)
  // Le groupe repris par un commercial n'est plus complétable par le visiteur, mais il reste
  // consultable : on garde son identifiant pour le lien de l'alerte, hors du souvenir courant.
  const [handover, setHandover] = useState<{ message: string | null; groupId: string | null } | null>(null)
  const [rememberedGroupId, setRememberedGroupId] = useState<string | null>(() => readRememberedGroupId())

  const cart = useCart(COLD_START_CART_SCOPE)
  // Coordonnées : préremplies par le lien, demandées avant le catalogue si le lien porte `identity=1`.
  const [identity, setIdentity] = useState<MarketplaceIdentity>(() => ({
    ...EMPTY_IDENTITY,
    ...readIdentityFromUrl(searchParams),
  }))
  const [identityConfirmed, setIdentityConfirmed] = useState(() => !identityRequested(searchParams))
  // La présélection ne rejoue pas à chaque rechargement du catalogue : le visiteur qui a retiré une
  // formation du panier ne doit pas la voir revenir en changeant de profession.
  const preselectionApplied = useRef(false)

  const query = useMemo(
    () => buildCatalogueQuery(searchParams, location.pathname).toString(),
    [searchParams, location.pathname],
  )

  const fetchCatalogue = useCallback(() => {
    setError(null)
    client
      .get(`/registration/content/marketplace/${query ? `?${query}` : ''}`)
      .then((resp) => {
        setPayload(resp.data as MarketplaceColdStartPayloadType)
      })
      .catch((err: AxiosError) => {
        setError('network')
        Bugsnag.notify(err)
        console.error(err)
      })
  }, [query])

  useEffect(() => {
    fetchCatalogue()
  }, [fetchCatalogue])

  // Une vue par chargement de page, pour compter qui arrive ici et qui continue. Sans effet sur la page
  // si l'appel échoue.
  const visitorId = useMemo(() => readVisitorId(), [])
  const visitRecorded = useRef(false)
  useEffect(() => {
    if (visitRecorded.current) return
    visitRecorded.current = true
    const payload = buildVisitPayload(new URLSearchParams(location.search), location.pathname, window.location.href, visitorId)
    // Coordonnées portées par le lien : enregistrées d'emblée, même sans formulaire à remplir.
    const fromUrl = readIdentityFromUrl(new URLSearchParams(location.search))
    client
      .post('/registration/content/marketplace_visit/', {
        ...payload,
        ...(hasIdentity(fromUrl) && !identityRequested(new URLSearchParams(location.search)) ? fromUrl : {}),
      })
      .catch(() => undefined)
  }, [location.search, location.pathname, visitorId])

  // Le panier suit la visite : on sait ce qui a été choisi même sans « Continuer ».
  const cartCodes = cart.items.map((item) => item.productCode).join(',')
  const cartSynced = useRef<string | null>(null)
  useEffect(() => {
    if (cartSynced.current === null) {
      cartSynced.current = cartCodes
      return undefined
    }
    if (cartSynced.current === cartCodes) return undefined
    const timer = window.setTimeout(() => {
      cartSynced.current = cartCodes
      client
        .post('/registration/content/marketplace_visit/', {
          visitor_id: visitorId,
          kind: 'cart',
          cart_product_codes: cartCodes ? cartCodes.split(',') : [],
        })
        .catch(() => undefined)
    }, 800)
    return () => window.clearTimeout(timer)
  }, [cartCodes, visitorId])

  const handleIdentitySubmit = (submitted: MarketplaceIdentity) => {
    setIdentity(submitted)
    setIdentityConfirmed(true)
    window.scrollTo(0, 0)
    client
      .post('/registration/content/marketplace_visit/', { visitor_id: visitorId, kind: 'identity', ...submitted })
      .catch(() => undefined)
  }

  // Un lien de diffusion de masse survit aux renommages de thèmes et aux copier-coller approximatifs :
  // les valeurs que le serveur a écartées sont la seule façon de voir qu'une campagne pointe à vide.
  useEffect(() => {
    if (!payload) return
    const ignored = flattenIgnoredParameters(payload.ignored_parameters)
    if (ignored.length === 0) return
    Bugsnag.notify(new Error('Marketplace cold start: paramètres ignorés'), (event) => {
      event.severity = 'info'
      event.addMetadata('marketplace', { ignored, url: window.location.href })
    })
  }, [payload])

  const { add: addToCart, remove: removeFromCart, items: cartItems } = cart

  useEffect(() => {
    if (!payload || preselectionApplied.current) return
    preselectionApplied.current = true
    payload.preselected_theme_codes.forEach((code) => {
      const theme = payload.themes.find((candidate) => candidate.theme_code === code)
      const item = theme ? toCartItem(theme) : null
      if (item) addToCart(item)
    })
  }, [payload, addToCart])

  // Réconciliation : le panier survit à un changement de profession ou de financeur, qui peut retirer
  // du catalogue une formation déjà sélectionnée. La garder mènerait à un refus au « Continuer ».
  useEffect(() => {
    if (!payload) return
    const available = new Set(payload.themes.map((theme) => theme.default_product?.product_code).filter(Boolean))
    cartItems.filter((item) => !available.has(item.productCode)).forEach((item) => removeFromCart(item.productCode))
  }, [payload, cartItems, removeFromCart])

  const displayDomain = resolveDisplayDomain(location.pathname, payload?.domain)

  const updateParameter = (name: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(name, value)
    } else {
      next.delete(name)
    }
    setSearchParams(next, { replace: true })
  }

  const handleToggleCart = (theme: MarketplaceThemeType) => {
    const item = toCartItem(theme)
    if (item) cart.toggle(item)
  }

  const lockedFunderCode = isLockedFunder(payload?.profile.funder?.code) ? payload?.profile.funder?.code : null
  // Financeur verrouillé : seules ses formations, et seul lui sur les cartes (pas ALMA, OPCO…).
  // Tant que le catalogue n'est pas chargé, le financeur est inconnu : un panier IPERIA déjà rempli ne doit pas
  // afficher son total le temps de la requête.
  const hidePrice = !payload || hidesPrice(lockedFunderCode)
  const visibleThemes = useMemo(() => restrictToFunder(payload?.themes ?? [], lockedFunderCode), [payload, lockedFunderCode])

  const handleContinue = (groupId: string | null = rememberedGroupId) => {
    if (cart.items.length === 0) return
    setSubmitting(true)
    setSubmitError(false)
    setSubmitErrorMessage(null)
    setHandover(null)

    client
      .post('/registration/registration_group/start/', {
        product_codes: cart.items.map((item) => item.productCode),
        profession: payload?.profile.profession?.code,
        domain: payload?.domain,
        registration_group: groupId,
        // Un financeur verrouillé (IPERIA) décide du tunnel : le serveur crée les dossiers directement dedans.
        funder: lockedFunderCode || undefined,
        visitor_id: visitorId,
        ...(hasIdentity(identity) ? identity : {}),
        ...readTrackingParameters(searchParams, window.location.href),
      })
      .then((resp) => {
        const group = resp.data as RegistrationGroupType
        rememberGroupId(group.global_id)
        setRememberedGroupId(group.global_id)
        cart.clear()
        navigate(`/groupe/${group.global_id}`)
      })
      .catch((err: AxiosError<{ detail?: string }>) => {
        setSubmitting(false)
        if (err.response?.status === 400 && groupId) {
          // Le groupe mémorisé ne peut pas accueillir ce panier (autre financeur) : on repart sur un neuf.
          forgetRememberedGroupId()
          setRememberedGroupId(null)
          handleContinue(null)
          return
        }
        if (err.response?.status === 403 && lockedFunderCode) {
          // Parcours pas encore ouvert : le souvenir du groupe n'y est pour rien.
          setSubmitError(true)
          setSubmitErrorMessage(err.response.data?.detail || null)
          return
        }
        if (err.response?.status === 403) {
          // Le groupe mémorisé est passé au commercial : on oublie le souvenir, le prochain essai
          // repartira sur un dossier neuf.
          forgetRememberedGroupId()
          setHandover({ message: err.response.data?.detail || null, groupId: rememberedGroupId })
          setRememberedGroupId(null)
          return
        }
        setSubmitError(true)
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  const renderThemeCard = (theme: MarketplaceThemeType, featured: boolean) => (
    <ThemeCard
      theme={theme}
      featured={featured}
      inCart={Boolean(theme.default_product && cart.has(theme.default_product.product_code))}
      alreadyRegistered={false}
      onOpen={() => setOpenedTheme(theme)}
      onToggleCart={() => handleToggleCart(theme)}
      hidePrice={hidePrice}
    />
  )

  const funder = payload?.profile.funder
  const profession = payload?.profile.profession

  return (
    <MarketplaceShell displayDomain={displayDomain} cartCount={identityConfirmed ? cart.count : 0}>
      {!identityConfirmed ? (
        <IdentityForm initial={identity} onSubmit={handleIdentitySubmit} />
      ) : (
      <>
      {identityRequested(searchParams) && hasIdentity(identity) && (
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {`${identity.first_name} ${identity.last_name} · ${identity.email} `}
          <Link component="button" variant="body2" onClick={() => setIdentityConfirmed(false)}>
            Modifier
          </Link>
        </Typography>
      )}
      {rememberedGroupId && (
        <Button component={RouterLink} to={`/groupe/${rememberedGroupId}`} sx={{ mb: 3 }}>
          Reprendre mes inscriptions
        </Button>
      )}

      {error === 'network' && (
        <Grid container direction="column" alignItems="center" spacing={2}>
          <Grid item>
            <Typography variant="h6" align="center">
              Une erreur est survenue pendant le chargement des formations.
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={fetchCatalogue}>
              Réessayer
            </Button>
          </Grid>
        </Grid>
      )}

      {!error && !payload && (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      )}

      {!error && payload && (
        <>
          <MarketplaceHero
            onDark={displayDomain === 'learning'}
            title="Composez votre parcours de formation"
            subtitle={
              funder
                ? `Les formations finançables par ${funder.name}. Nos apprenants en retiennent ${cart.target} en moyenne.`
                : `Profitez de vos financements pour vous former sur plusieurs thèmes. Nos apprenants en retiennent ${cart.target} en moyenne.`
            }
            selectedCount={cart.count}
            target={cart.target}
          >
            {!profession && !lockedFunderCode && (
              <ProfessionPicker
                value={null}
                onChange={(selected: ProfessionType | null) => updateParameter('profession', selected?.code || null)}
                onDark={displayDomain === 'learning'}
              />
            )}
          </MarketplaceHero>

          {handover && (
            <Alert severity="warning" sx={{ mb: 4 }}>
              {handover.message}{' '}
              {handover.groupId && (
                <Link component={RouterLink} to={`/groupe/${handover.groupId}`}>
                  Voir mes inscriptions
                </Link>
              )}
            </Alert>
          )}

          <CatalogSections
            themes={visibleThemes}
            renderThemeCard={renderThemeCard}
            featuredThemeCodes={payload.featured_theme_codes}
            banner={
              funder && (
                // Le lien tient sa promesse -- le catalogue est bien restreint au financeur annoncé --
                // sans enfermer le visiteur qui en dépend d'un autre, sauf financeur verrouillé (IPERIA).
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'inline' }}>
                    {`Vous voyez les formations finançables par ${funder.name}. `}
                  </Typography>
                  {!lockedFunderCode && (
                    <Link component="button" variant="body2" onClick={() => updateParameter('funder', null)}>
                      Voir toutes les formations
                    </Link>
                  )}
                </Box>
              )
            }
            emptyState={
              <Grid container direction="column" alignItems="center" spacing={2}>
                <Grid item>
                  <Typography variant="body1" align="center">
                    Aucune formation ne correspond à ce profil pour le moment.
                  </Typography>
                </Grid>
                {(funder || profession) && (
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        // Aucun élargissement automatique côté serveur : c'est un geste du visiteur.
                        const next = new URLSearchParams(searchParams)
                        if (!lockedFunderCode) next.delete('funder')
                        next.delete('profession')
                        next.delete('themes')
                        setSearchParams(next, { replace: true })
                      }}
                    >
                      Voir toutes les formations
                    </Button>
                  </Grid>
                )}
              </Grid>
            }
          />
        </>
      )}

      <ProductDetailModal
        theme={openedTheme}
        open={Boolean(openedTheme)}
        inCart={Boolean(openedTheme?.default_product && cart.has(openedTheme.default_product.product_code))}
        alreadyRegistered={false}
        onClose={() => setOpenedTheme(null)}
        onToggleCart={() => {
          if (openedTheme) handleToggleCart(openedTheme)
        }}
        hidePrice={hidePrice}
      />

      <CartBar
        hidePrice={hidePrice}
        items={cart.items}
        target={cart.target}
        submitting={submitting}
        error={submitError}
        errorMessage={submitErrorMessage}
        onRemove={cart.remove}
        onContinue={() => handleContinue()}
      />
      </>
      )}
    </MarketplaceShell>
  )
}

export default MarketplaceColdStart
